import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Eye, Loader2, MessageSquare, Send } from 'lucide-react';
import {
  useMessages,
  useMessageThread,
  useReplyMessage,
  type GuestMessage,
} from '../../../hooks/useManagement';
import PropertySelector from '../../../components/PropertySelector';
import GuestMessagesPreview from '../../../components/GuestMessagesPreview';

type ChannelKey = GuestMessage['channel'];

const channelStyles: Record<ChannelKey, string> = {
  direct: 'bg-teal-100 text-teal-700',
  airbnb: 'bg-rose-100 text-rose-700',
  booking: 'bg-blue-100 text-blue-700',
};

const fmtFull = (iso: string) => format(parseISO(iso), "d 'de' MMMM, HH:mm", { locale: es });
const fmtShort = (iso: string) => format(parseISO(iso), 'd MMM', { locale: es });

export default function ManageMessagesPage() {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const { data: messages, isLoading } = useMessages();

  const unread = (messages ?? []).filter((m) => m.is_read === 0).length;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.manage.messages.title')}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {unread > 0 ? t('portal.manage.messages.unread', { count: unread }) : t('portal.manage.messages.allDone')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowPreview(true)}
            className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 px-3.5 py-2 rounded-xl transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>{t('portal.manage.messages.previewButton', 'Vista previa de los mensajes al huésped')}</span>
          </button>
          <PropertySelector allowAll />
        </div>
      </div>

      {/* Explainer: these previews show the automated pre-arrival / post-stay
          messages and the booking emails exactly as guests receive them. */}
      <p className="text-xs text-slate-500 -mt-2">
        {t(
          'portal.manage.messages.previewHint',
          'Incluye los mensajes automáticos de pre-llegada y post-estancia, y los emails de reserva.',
        )}
      </p>

      {showPreview && <GuestMessagesPreview onClose={() => setShowPreview(false)} />}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-teal-500" size={32} />
          </div>
        ) : !messages || messages.length === 0 ? (
          <div className="p-12 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
              <MessageSquare className="w-6 h-6" />
            </div>
            <p className="text-slate-900 font-medium">{t('portal.manage.messages.emptyTitle')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('portal.manage.messages.emptySub')}</p>
          </div>
        ) : (
          <div className="flex min-h-[34rem]">
            {/* LEFT: list */}
            <div
              className={`w-full md:w-80 md:shrink-0 md:border-r border-slate-100 divide-y divide-slate-100 overflow-y-auto ${
                selectedId !== null ? 'hidden md:block' : 'block'
              }`}
            >
              {messages.map((msg) => {
                const unreadMsg = msg.is_read === 0;
                const active = msg.id === selectedId;
                return (
                  <button
                    key={msg.id}
                    onClick={() => setSelectedId(msg.id)}
                    className={`w-full flex items-start gap-3 px-4 py-4 text-left transition-colors ${
                      active ? 'bg-teal-50/70' : unreadMsg ? 'bg-teal-50/30 hover:bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold ${channelStyles[msg.channel]}`}
                    >
                      {msg.guest_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`text-sm truncate ${unreadMsg ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}
                        >
                          {msg.guest_name}
                        </span>
                        <span className="text-xs text-slate-400 shrink-0">{fmtShort(msg.created_at)}</span>
                      </div>
                      <div
                        className={`text-sm truncate mt-0.5 ${unreadMsg ? 'text-slate-800 font-medium' : 'text-slate-500'}`}
                      >
                        {msg.subject ?? t('portal.manage.messages.noSubject')}
                      </div>
                      {msg.preview && (
                        <div className="text-xs text-slate-400 truncate mt-0.5">{msg.preview}</div>
                      )}
                    </div>
                    {unreadMsg && <div className="w-2 h-2 bg-teal-500 rounded-full shrink-0 mt-2" />}
                  </button>
                );
              })}
            </div>

            {/* RIGHT: detail */}
            <div className={`flex-1 min-w-0 ${selectedId !== null ? 'block' : 'hidden md:block'}`}>
              {selectedId === null ? (
                <div className="hidden md:flex flex-col items-center justify-center h-full text-center p-12">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <p className="text-sm text-slate-500">{t('portal.manage.messages.selectPrompt')}</p>
                </div>
              ) : (
                <ThreadDetail id={selectedId} onBack={() => setSelectedId(null)} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ThreadDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const { t } = useTranslation();
  const channelLabels: Record<ChannelKey, string> = {
    direct: t('portal.manage.channel.direct'),
    airbnb: t('portal.manage.channel.airbnb'),
    booking: t('portal.manage.channel.bookingShort'),
  };
  const { data, isLoading } = useMessageThread(id);
  const reply = useReplyMessage();
  const [body, setBody] = useState('');

  const send = () => {
    const trimmed = body.trim();
    if (!trimmed || reply.isPending) return;
    reply.mutate(
      { id, body: trimmed },
      { onSuccess: () => setBody('') },
    );
  };

  if (isLoading || !data) {
    return (
      <div className="flex justify-center items-center h-full py-20">
        <Loader2 className="animate-spin text-teal-500" size={32} />
      </div>
    );
  }

  const { message, replies } = data;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-4 border-b border-slate-100 bg-slate-50">
        <button
          onClick={onBack}
          className="md:hidden p-1.5 hover:bg-slate-200 rounded-lg text-slate-500 shrink-0"
          aria-label={t('portal.manage.messages.back')}
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 truncate">{message.subject ?? t('portal.manage.messages.noSubject')}</h3>
          <p className="text-xs text-slate-500 truncate">
            {message.guest_name}
            {message.guest_email ? ` · ${message.guest_email}` : ''}
          </p>
        </div>
        <span
          className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${channelStyles[message.channel]}`}
        >
          {channelLabels[message.channel]}
        </span>
      </div>

      {/* Conversation */}
      <div className="flex-1 p-4 sm:p-6 overflow-y-auto space-y-4">
        {/* Original message */}
        <div className="bg-slate-50 rounded-xl p-4 max-w-xl">
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
            {message.body ?? message.preview ?? ''}
          </p>
          <p className="text-xs text-slate-400 mt-3">{fmtFull(message.created_at)}</p>
        </div>

        {/* Replies */}
        {replies.map((r) =>
          r.from_admin === 1 ? (
            <div key={r.id} className="flex justify-end">
              <div className="bg-teal-600 text-white rounded-xl px-4 py-3 max-w-xs sm:max-w-md">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{r.body}</p>
                <p className="text-teal-100 text-xs mt-1.5 text-right">{fmtFull(r.created_at)}</p>
              </div>
            </div>
          ) : (
            <div key={r.id} className="bg-slate-50 rounded-xl p-4 max-w-xl">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{r.body}</p>
              <p className="text-xs text-slate-400 mt-3">{fmtFull(r.created_at)}</p>
            </div>
          ),
        )}
      </div>

      {/* Reply box */}
      <div className="p-4 border-t border-slate-100">
        <div className="flex gap-3">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('portal.manage.messages.replyPlaceholder')}
            rows={3}
            className="flex-1 border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
          />
          <button
            onClick={send}
            disabled={!body.trim() || reply.isPending}
            className="self-end flex items-center gap-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-3 rounded-xl transition-colors"
          >
            {reply.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span className="hidden sm:inline">{t('portal.manage.messages.send')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
